import axios from 'axios';
import { createApiClient, createTokenStorage, createSingleFlight } from '@applauss/api-client';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000';

// Auth token management — key names are load-bearing: changing them logs out
// every existing admin session
const storage = createTokenStorage(
  {
    accessToken: 'admin_token',
    refreshToken: 'admin_refresh_token',
    user: 'admin_user',
  },
  { removeCorruptUser: true }
);

export const getToken = () => storage.getAccessToken();
export const getRefreshToken = () => storage.getRefreshToken();
export const getUser = () => storage.getUser();

export const saveAuth = (data) => {
  storage.setTokens({
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
  });
  if (data.user) {
    storage.setUser(data.user);
  }
};

export const clearAuth = () => storage.clear();

const redirectToLogin = () => {
  window.location.href = '/login';
};

// Single-flight so parallel 401s share one /auth/refresh call
const refreshAccessToken = createSingleFlight(async () => {
  const refreshToken = storage.getRefreshToken();
  if (!refreshToken) {
    clearAuth();
    redirectToLogin();
    throw new Error('No refresh token available');
  }

  try {
    const res = await axios.post(`${API_BASE_URL}/auth/refresh`, { refresh_token: refreshToken });
    saveAuth(res.data);
    return res.data.access_token;
  } catch (err) {
    clearAuth();
    redirectToLogin();
    throw err;
  }
});

// Axios instance: bearer token from storage, refresh-and-retry on 401, always English
const api = createApiClient({
  baseURL: API_BASE_URL,
  timeout: 30000,
  // Admin is English-only. Without this the browser sends the OS language, and the
  // public endpoints admin reads (/search) would answer in it.
  getLanguage: () => 'en',
  getAccessToken: storage.getAccessToken,
  refreshOn401: { execute: refreshAccessToken },
});

// Auth API
export const authApi = {
  login: async (email, password) => {
    const response = await api.post('/auth/login/email', { email, password });
    return response.data;
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Logout even if the API call fails
    }
    clearAuth();
  },

  validate: async () => {
    const response = await api.get('/auth/validate');
    return response.data;
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
    return response.data;
  }
};

// Events API
// GET  /admin/events                -> { events: [...], pagination: { page, page_size, total, total_pages } }
// GET  /admin/events/{id}           -> { event: {...} }
// PUT  /admin/events/{id}           -> { success, message, updated_fields }
// DELETE /admin/events/{id}?confirm -> { success, message }
// GET  /search?query=...&limit=N    -> { query, filters, total, events: [...] }
// Classify / Re-classify one event, artist or venue: one LLM call, synchronous. 200 whether or not
// the classifier produced a classification — read `success`, `provider_errors` and `ambiguous` off
// the body. Past the instance's 30s default: with web search and a retry the call can run longer,
// and giving up early reports a failure for a classification the server still stores and bills.
const classifyEntity = async (kind, id) => {
  const response = await api.post(`/admin/${kind}/${id}/classify`, null, { timeout: 180000 });
  return response.data;
};

export const eventsApi = {
  // `config` forwards axios options — pass { signal } to abort a superseded
  // search-as-you-type request instead of just discarding its response.
  getAll: async (params = {}, config = {}) => {
    const response = await api.get('/admin/events', { params, ...config });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/admin/events/${id}`);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/admin/events/${id}`, data);
    return response.data;
  },

  // Billing position of one artist already on the event. Its own endpoint because the event PUT
  // carries no `artists`: sending the whole line-up back to be diffed would let a stale copy of it
  // drop an act somebody else added. `rank` null clears it — an unknown billing order is a real
  // answer, and `headliner` follows the rank server-side.
  setArtistRank: async (eventId, artistId, rank) => {
    const response = await api.patch(`/admin/events/${eventId}/artists/${artistId}/rank`, {
      performance_rank: rank
    });
    return response.data;
  },

  // Put an existing catalogue artist on the event's line-up; `rank` null leaves the billing order unknown.
  addArtist: async (eventId, artistId, rank = null) => {
    const response = await api.post(`/admin/events/${eventId}/artists`, {
      artist_id: artistId,
      performance_rank: rank
    });
    return response.data;
  },

  // Hide one artist from the line-up: off it for every reader, kept on the event in
  // `hidden_artists` (who, when) so a re-fetch does not bring it back. `addArtist` restores it.
  hideArtist: async (eventId, artistId) => {
    const response = await api.post(`/admin/events/${eventId}/artists/${artistId}/hide`);
    return response.data;
  },

  hide: async (id) => {
    const response = await api.post(`/admin/events/${id}/hide`);
    return response.data;
  },

  unhide: async (id) => {
    const response = await api.post(`/admin/events/${id}/unhide`);
    return response.data;
  },

  activate: async (id) => {
    const response = await api.post(`/admin/events/${id}/activate`);
    return response.data;
  },

  classify: (id) => classifyEntity("events", id),

  delete: async (id) => {
    const response = await api.delete(`/admin/events/${id}`, {
      params: { confirm: true }
    });
    return response.data;
  },

  search: async (query, params = {}) => {
    const response = await api.get('/search', {
      params: { query, ...params }
    });
    return response.data;
  },

  getScanCities: async () => {
    const response = await api.get('/admin/events/scan-cities');
    return Array.isArray(response.data) ? response.data : response.data.cities || [];
  },

  getSuspiciousDuplicates: async (cities = null) => {
    const params = cities && cities.length ? { cities: cities.join(',') } : {};
    const response = await api.get('/admin/events/suspicious-duplicates', { params });
    return response.data;
  },

  getPossibleDuplicates: async (page = 1, pageSize = 20, search = null, city = null) => {
    const params = { page, page_size: pageSize };
    if (search) params.search = search;
    if (city) params.city = city;
    const response = await api.get('/admin/events/possible-duplicates', { params });
    return response.data;
  },

  getMergePreview: async (sourceId, targetId, mergeOccurrences = true, occurrenceName = null) => {
    const params = { merge_occurrences: mergeOccurrences };
    if (occurrenceName !== null && occurrenceName !== '') params.occurrence_name = occurrenceName;
    const response = await api.get(`/admin/events/${sourceId}/merge-preview/${targetId}`, { params });
    return response.data;
  },

  mergeInto: async (sourceId, targetId, mergeOccurrences = true, occurrenceName = null) => {
    const params = { merge_occurrences: mergeOccurrences };
    if (occurrenceName !== null && occurrenceName !== '') params.occurrence_name = occurrenceName;
    const response = await api.post(`/admin/events/${sourceId}/merge-into/${targetId}`, null, { params });
    return response.data;
  },

  markDifferent: async (eventId, otherEventId) => {
    const response = await api.post(`/admin/events/${eventId}/mark-different/${otherEventId}`);
    return response.data;
  },

  getFlaggedDuplicates: async (params = {}) => {
    const response = await api.get('/admin/events/flagged-duplicates', { params });
    return response.data;
  },

  undoDuplicate: async (eventId) => {
    const response = await api.post(`/admin/events/${eventId}/undo-duplicate`);
    return response.data;
  },
};

// Image Review API
// GET  /admin/events/image-review                          -> { events, total, event_image_fallback_count, artist_fallback_count, bad_image_count }
// GET  /admin/events/image-review/count                    -> { total }
// GET  /admin/events/:id/image-suggestions                 -> { suggestions, query, diagnostics, search_available }
// POST /admin/events/:id/resolve-image-review?note=...    -> { success, event_id }
// POST /admin/events/:id/dismiss-image-review?note=...    -> { success, event_id }
// GET  /admin/events/image-review/flagged-urls            -> { entries: [{ url, event_count, events,
//        source: 'review' | 'detected' | 'allowed', first_detected_at, allowed_by, allowed_at }], total }
// POST /admin/events/image-blocklist                       -> { success, requested, added }
// POST /admin/events/image-allowlist                       -> { success, requested, allowed, reselected, capped }
// DELETE /admin/events/image-allowlist?url=...             -> { success, requested, removed }
export const imageReviewApi = {
  getCount: async () => {
    const response = await api.get('/admin/events/image-review/count');
    return response.data;
  },

  getQueue: async (limit = 100, city = null, search = null) => {
    const params = { limit };
    if (city) params.city = city;
    if (search?.trim()) params.search = search.trim();
    const response = await api.get('/admin/events/image-review', { params });
    return response.data;
  },

  getDirectSuggestions: async (eventId, limit = 6) => {
    const params = { limit };
    const response = await api.get(`/admin/events/${eventId}/image-suggestions`, { params });
    return response.data;
  },

  searchBrave: async (eventId, query = null, limit = 6) => {
    const params = { limit };
    if (query?.trim()) params.query = query.trim();
    const response = await api.get(`/admin/events/${eventId}/image-search`, { params });
    return response.data;
  },

  resolve: async (eventId, note = null) => {
    const params = note ? { note } : {};
    const response = await api.post(`/admin/events/${eventId}/resolve-image-review`, null, { params });
    return response.data;
  },

  dismiss: async (eventId, note = null) => {
    const params = note ? { note } : {};
    const response = await api.post(`/admin/events/${eventId}/dismiss-image-review`, null, { params });
    return response.data;
  },

  getBlocklist: async (page = 1, pageSize = 100) => {
    const response = await api.get('/admin/events/image-blocklist', {
      params: { page, page_size: pageSize },
    });
    return response.data;
  },

  // Return the subset of the given image URLs that are known placeholders
  // (on the confirmed blocklist or flagged in an open image-review ticket).
  checkSuspicious: async (urls) => {
    const list = (urls || []).filter(Boolean);
    if (list.length === 0) return [];
    const response = await api.post('/admin/images/check-suspicious', { urls: list });
    return response.data.suspicious || [];
  },

  // Entries carry `events` (id + titles + status, capped per row) alongside
  // `event_count`, which stays the untruncated total.
  getFlaggedUrls: async (page = 1, pageSize = 25) => {
    const response = await api.get('/admin/events/image-review/flagged-urls', {
      params: { page, page_size: pageSize },
    });
    return response.data;
  },

  // Confirm URLs as placeholders without resolving any single event's review —
  // the per-event resolve flow only reaches events left with no clean image.
  // Idempotent: re-blocking an existing URL returns added: 0.
  blockUrls: async (urls) => {
    const list = (urls || []).filter(Boolean);
    if (list.length === 0) return { success: true, requested: 0, added: 0 };
    const response = await api.post('/admin/events/image-blocklist', { urls: list });
    return response.data;
  },

  // Clear one URL as a false positive — a real image the heuristic flagged. Durable:
  // the scan subtracts allowed URLs, so it cannot re-detect it next run. This is also
  // how a blocked URL is released: deleting the blocklist row instead would leave the
  // next scan free to detect and demote it again. Idempotent: re-clearing returns 0.
  allowUrl: async (url) => {
    const response = await api.post('/admin/events/image-allowlist', { urls: [url] });
    return response.data;
  },

  // Withdraw a clearing: the URL goes back to `detected`, so it is demoted again at once
  // and stays on the flagged list. Not the same as blocking it.
  unallowUrl: async (url) => {
    const response = await api.delete('/admin/events/image-allowlist', { params: { url } });
    return response.data;
  },

};

// Users API
// GET    /admin/users                    -> { users: [...], total, limit, offset }
// DELETE /admin/users/:id                -> { success, message }
// PUT    /admin/users/:id/role?role=xxx  -> { success, message }
export const usersApi = {
  getAll: async (params = {}) => {
    const response = await api.get('/admin/users', { params });
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/admin/users/${id}`);
    return response.data;
  },

  setRole: async (id, role) => {
    const response = await api.put(`/admin/users/${id}/role`, null, {
      params: { role }
    });
    return response.data;
  }
};

// Reports API
// GET    /admin/reports?page=&page_size=&status=&entity_type= -> { reports: [...], pagination }
// GET    /admin/reports/stats       -> { open_count, resolved_count, dismissed_count, total }
// GET    /admin/reports/{id}        -> { report: {...} }
// POST   /reports                   -> { id, entity_type, entity_id, reason, status, created_at }
// PUT    /admin/reports/{id}        -> { success, message, report_id, updated_fields }
// POST   /admin/reports/{id}/accept-price -> accepts proposed event start_price
// DELETE /admin/reports/{id}?confirm -> { success, message, report_id }
export const reportsApi = {
  getAll: async (params = {}) => {
    const response = await api.get('/admin/reports', { params });
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/admin/reports/stats');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/admin/reports/${id}`);
    return response.data;
  },

  // Deliberately NOT /admin/reports: the API has no admin create endpoint. The
  // consumer route accepts any valid JWT (validate_jwt does not check role), so the
  // admin token works and the report is attributed to the acting admin's user_id —
  // it lists with their email as the reporter, not as a system report. The route
  // 400s on system-generated reasons and 404s on an entity that isn't indexed.
  create: async (data) => {
    const response = await api.post('/reports', data);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/admin/reports/${id}`, data);
    return response.data;
  },

  acceptProposal: async (id, url) => {
    const response = await api.post(`/admin/reports/${id}/accept-proposal`, { url });
    return response.data;
  },




  delete: async (id) => {
    const response = await api.delete(`/admin/reports/${id}`, {
      params: { confirm: true }
    });
    return response.data;
  },
};

// Event research records (`event_research` table, migration 025) — everything one agent pass
// learned about one event, worked as a unit. Not reports: a report is one claim with one
// lifecycle, a record is a whole event's findings with per-item acceptance.
//
// GET  /admin/event-research?status&search&city&page&page_size -> { records, pagination }
// POST /admin/event-research/{id}/accept-url    -> adds a candidate URL to the event, or to one date
// POST /admin/event-research/{id}/accept-artist -> attaches/creates the artist on the event
// POST /admin/event-research/{id}/accept-price  -> writes event.start_price (409 if newer)
// POST /admin/event-research/{id}/accept-description -> writes event.ai_description
// POST /admin/event-research/{id}/accept-title  -> writes event.preferred_title, locked (#814)
// POST /admin/event-research/{id}/status        -> resolve / dismiss by hand
export const eventResearchApi = {
  getAll: async (params = {}) => {
    const response = await api.get('/admin/event-research', { params });
    return response.data;
  },

  // `occurrenceId` files the link against ONE of the event's dates instead of the event as a
  // whole — a multi-date event is routinely sold one page per night, and the consumer shows a
  // date's own links when it has them. null/undefined = the event, which is every date.
  acceptUrl: async (id, url, occurrenceId = null) => {
    const response = await api.post(`/admin/event-research/${id}/accept-url`, {
      url,
      occurrence_id: occurrenceId
    });
    return response.data;
  },

  // Any URL, not only one the record proposed — the agent routinely lands on the venue's
  // programme index or a provider's city page instead of this event's own. Same http(s)/resale/
  // duplicate rules server-side; nothing is stamped, so it never auto-resolves the record.
  addUrl: async (id, url, occurrenceId = null) => {
    const response = await api.post(`/admin/event-research/${id}/add-url`, { url, occurrence_id: occurrenceId });
    return response.data;
  },

  // Undo for either add path. Only sticks for a URL ingestion never supplied — a provider's own
  // link returns on the next fetch, and `bad` in the events editor is the durable hide for those.
  // Takes it off that date when `occurrenceId` is given, off the event's own list otherwise. A
  // resale link may be removed from a date even though none can be added: ingestion writes them
  // per date (JamBase supplies StubHub and viagogo), and clearing one is the point of the screen.
  removeUrl: async (id, url, occurrenceId = null) => {
    const response = await api.post(`/admin/event-research/${id}/remove-url`, {
      url,
      occurrence_id: occurrenceId
    });
    return response.data;
  },

  // For the pass that found no price at all. `acceptPrice` needs a proposal to stamp; this one
  // takes the admin's own amount and currency, usually read off the agent's `notes`.
  setPrice: async (id, price, currency) => {
    const response = await api.post(`/admin/event-research/${id}/set-price`, { price, currency });
    return response.data;
  },

  // `domain` is the kept subset of what the record proposed (inherited from the event); [] creates
  // the artist with none. The server rejects any value the record did not list.
  // `artistId` attaches an existing catalogue artist instead of creating one — the id of a
  // near-name match the admin picked in ArtistMatchModal. Null creates, as before.
  acceptArtist: async (id, name, performanceRank = null, domain = null, artistId = null) => {
    const response = await api.post(`/admin/event-research/${id}/accept-artist`, {
      name,
      artist_id: artistId,
      performance_rank: performanceRank,
      domain
    });
    return response.data;
  },

  acceptPrice: async (id, price, currency) => {
    const response = await api.post(`/admin/event-research/${id}/accept-price`, { price, currency });
    return response.data;
  },

  // Writes event.ai_description, which consumers show instead of the ingested `description`.
  // `text` is the admin's edit; null accepts the proposal verbatim. The ingested description is
  // left alone, so clearing ai_description later is a real undo.
  acceptDescription: async (id, text = null) => {
    const response = await api.post(`/admin/event-research/${id}/accept-description`, { text });
    return response.data;
  },

  // Writes event.preferred_title and locks it against re-fetch (#814). `title` is the admin's edit;
  // null accepts the proposal. A title an admin locked on the event is a 409; a provider's is replaced.
  acceptTitle: async (id, title = null) => {
    const response = await api.post(`/admin/event-research/${id}/accept-title`, { title });
    return response.data;
  },

  // Write event.duration_minutes / event.min_age (#792): `ask` is "duration" or "min_age", `key`
  // the body field ("minutes" / "age"). The value is the admin's correction; null accepts the
  // proposal. A different value already on the event is a 409, not an overwrite.
  acceptStatedValue: async (id, ask, key, value = null) => {
    const response = await api.post(`/admin/event-research/${id}/accept-${ask.replace("_", "-")}`, { [key]: value });
    return response.data;
  },

  // Write the proposed doors / set times onto the event's dates (#792): `ask` is "doors" or
  // "set_times". Accepted as a whole; fill-only, so a date already holding one keeps it.
  acceptTimes: async (id, ask) => {
    const response = await api.post(`/admin/event-research/${id}/accept-${ask.replace("_", "-")}`, {});
    return response.data;
  },

  // Mark the dates the record says were cancelled (every live date when it names none) cancelled
  // on the event. Ingestion keeps them cancelled; the events editor un-cancels one.
  acceptCancelled: async (id) => {
    const response = await api.post(`/admin/event-research/${id}/accept-cancelled`, {});
    return response.data;
  },

  setStatus: async (id, status) => {
    const response = await api.post(`/admin/event-research/${id}/status`, { status });
    return response.data;
  },
};

// Artist research records (`artist_research` table, migration 030) — the artist-side twin of
// event research. One agent pass over one artist: the links it found and the MusicBrainz entry
// it found, each accepted on its own. Why it exists: apps/api/domains/reports/CLAUDE.md.
//
// GET  /admin/artist-research?status&search&page&page_size -> { records, pagination }
// POST /admin/artist-research/{id}/accept-link        -> writes artist.links[platform] (+ source_ids)
// POST /admin/artist-research/{id}/add-link           -> the admin's own link, stamps nothing
// POST /admin/artist-research/{id}/remove-link        -> the undo for either add path
// POST /admin/artist-research/{id}/accept-musicbrainz -> assigns the proposed MBID
// POST /admin/artist-research/{id}/status             -> resolve / dismiss by hand
export const artistResearchApi = {
  getAll: async (params = {}) => {
    const response = await api.get('/admin/artist-research', { params });
    return response.data;
  },

  // `url` must be one the record proposed — the server refuses anything else, which is what stops
  // an arbitrary URL becoming the identity an artist's photograph is fetched from.
  acceptLink: async (id, url) => {
    const response = await api.post(`/admin/artist-research/${id}/accept-link`, { url });
    return response.data;
  },

  // A page the agent missed. Skips the whitelist, not the validation: the platform must be a key
  // the codebase understands and the URL must be on that platform's own domain. Stamps nothing,
  // so it never auto-resolves the record.
  addLink: async (id, platform, url) => {
    const response = await api.post(`/admin/artist-research/${id}/add-link`, { platform, url });
    return response.data;
  },

  removeLink: async (id, platform) => {
    const response = await api.post(`/admin/artist-research/${id}/remove-link`, { platform });
    return response.data;
  },

  // Runs the full change-musicbrainz-id cascade server-side: re-enrich, image discovery,
  // classification, propagation, and an auto-merge if the id is already on another artist (the
  // response then carries `merged` and `canonical_id`). The id is the record's, never the client's.
  acceptMusicbrainz: async (id) => {
    const response = await api.post(`/admin/artist-research/${id}/accept-musicbrainz`, {});
    return response.data;
  },

  setStatus: async (id, status) => {
    const response = await api.post(`/admin/artist-research/${id}/status`, { status });
    return response.data;
  },
};

// Artists API
// GET    /admin/artists             -> { artists: [...], pagination }
// GET    /admin/artists/{id}        -> { artist: {...} }
// PUT    /admin/artists/{id}        -> { success, message, updated_fields }
// GET    /admin/artists/deleted    -> { artists: [...], pagination }
// DELETE /admin/artists/{id}            -> 409 + impact preview while anything references it
// DELETE /admin/artists/{id}?confirm    -> { success, message } (soft delete + cascade)
export const artistsApi = {
  classify: (id) => classifyEntity("artists", id),

  getAll: async (params = {}, config = {}) => {
    const response = await api.get('/admin/artists', { params, ...config });
    return response.data;
  },

  // Distinct `domain` values, for the list's type filter. Aggregated server-side so the
  // options track what ingestion actually produced.
  getDomains: async (config = {}) => {
    const response = await api.get('/admin/artists/domains', config);
    return response.data;
  },

  // Catalogue artists whose name all but matches a proposed one. Shown before accepting a
  // research-record artist so the admin can attach the act we already hold instead of creating a
  // near-duplicate — the check that used to happen (by MBID) at accept time, now done by a human.
  nameMatches: async (name, config = {}) => {
    const response = await api.get('/admin/artists/name-matches', { params: { name }, ...config });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/admin/artists/${id}`);
    return response.data;
  },

  // Soft-deleted artists (deleted_at / deleted_by_email), newest first
  getDeleted: async (params = {}) => {
    const response = await api.get('/admin/artists/deleted', { params });
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/admin/artists/${id}`, data);
    return response.data;
  },

  // Two-step, enforced server-side: called without `confirm` it writes nothing and 409s with
  // the impact preview (events, favorites, open reports) whenever anything still references
  // the artist. Repeat with confirm to go through. An unreferenced artist deletes first time.
  delete: async (id, { confirm = false } = {}) => {
    const response = await api.delete(`/admin/artists/${id}`, {
      params: confirm ? { confirm: true } : {},
    });
    return response.data;
  },

  checkMusicbrainzId: async (musicbrainzId, excludeArtistId = null) => {
    const response = await api.get(
      `/admin/artists/check-musicbrainz/${encodeURIComponent(musicbrainzId)}`,
      { params: excludeArtistId ? { exclude_artist_id: excludeArtistId } : {} }
    );
    return response.data;
  },

  changeMusicbrainzId: async (id, musicbrainzId, keepClassification = false) => {
    const response = await api.post(`/admin/artists/${id}/change-musicbrainz-id`, {
      musicbrainz_id: musicbrainzId,
      keep_classification: keepClassification,
    });
    return response.data;
  },

  // Artist duplicate review
  getDuplicates: async (page = 1, pageSize = 20, search = null) => {
    const params = { page, page_size: pageSize };
    if (search) params.search = search;
    const response = await api.get('/admin/artists/possible-duplicates', { params });
    return response.data;
  },

  mergeInto: async (sourceId, canonicalId) => {
    const response = await api.post(`/admin/artists/${sourceId}/merge-into/${canonicalId}`);
    return response.data;
  },

  markDifferent: async (artistId, otherArtistId) => {
    const response = await api.post(`/admin/artists/${artistId}/mark-different/${otherArtistId}`);
    return response.data;
  },

  getFlaggedDuplicates: async (page = 1, pageSize = 20, search = null, flaggedBy = null) => {
    const params = { page, page_size: pageSize };
    if (search) params.search = search;
    if (flaggedBy) params.flagged_by = flaggedBy;
    const response = await api.get('/admin/artists/flagged-duplicates', { params });
    return response.data;
  },

  undoMerge: async (artistId) => {
    const response = await api.post(`/admin/artists/${artistId}/undo-merge`);
    return response.data;
  },
};

// Venues API
// GET    /admin/venues/capacity-buckets -> { buckets: [{name, min, max, label}, ...] }
// GET    /admin/venues              -> { venues: [...], pagination }
// GET    /admin/venues/{id}         -> { venue: {...} }
// PUT    /admin/venues/{id}         -> { success, message, updated_fields }
// DELETE /admin/venues/{id}?confirm&force -> { success, message }
// GET    /admin/venues/possible-duplicates?city= -> { groups: [...], total_groups, pagination }
// POST   /admin/venues/{sourceId}/merge-into/{canonicalId} -> { success, message, canonical_id, merged_id, events_repointed, source_id_conflicts }
// POST   /admin/venues/{venueId}/mark-different/{otherVenueId} -> { success, message }
// GET    /admin/venues/flagged-duplicates -> { pairs: [...], pagination, flaggers }
export const venuesApi = {
  classify: (id) => classifyEntity("venues", id),

  getCapacityBuckets: async () => {
    const response = await api.get('/admin/venues/capacity-buckets');
    return response.data.buckets; // [{name, min, max, label}]
  },

  getAll: async (params = {}, config = {}) => {
    const response = await api.get('/admin/venues', { params, ...config });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/admin/venues/${id}`);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/admin/venues/${id}`, data);
    return response.data;
  },

  verify: async (id, verified) => {
    const response = await api.patch(`/admin/venues/${id}/verify`, { verified });
    return response.data;
  },

  delete: async (id, force = false) => {
    const response = await api.delete(`/admin/venues/${id}`, {
      params: { confirm: true, ...(force && { force: true }) }
    });
    return response.data;
  },

  // Venue duplicate review
  getDuplicates: async (page = 1, pageSize = 20, search = null, city = null) => {
    const params = { page, page_size: pageSize };
    if (search) params.search = search;
    if (city) params.city = city;
    const response = await api.get('/admin/venues/possible-duplicates', { params });
    return response.data;
  },

  mergeInto: async (sourceId, canonicalId) => {
    const response = await api.post(`/admin/venues/${sourceId}/merge-into/${canonicalId}`);
    return response.data;
  },

  markDifferent: async (venueId, otherVenueId) => {
    const response = await api.post(`/admin/venues/${venueId}/mark-different/${otherVenueId}`);
    return response.data;
  },

  // Rooms: link two venues as rooms of one place (choice picks the roles when the API asks),
  // or detach a room. `dryRun` previews without writing.
  link: async (venueId, otherVenueId, { choice = null, dryRun = false } = {}) => {
    const response = await api.post(`/admin/venues/${venueId}/link/${otherVenueId}`, {
      choice,
      dry_run: dryRun,
    });
    return response.data;
  },

  detach: async (venueId) => {
    const response = await api.post(`/admin/venues/${venueId}/detach`);
    return response.data;
  },

  getFlaggedDuplicates: async (page = 1, pageSize = 20, search = null, flaggedBy = null) => {
    const params = { page, page_size: pageSize };
    if (search) params.search = search;
    if (flaggedBy) params.flagged_by = flaggedBy;
    const response = await api.get('/admin/venues/flagged-duplicates', { params });
    return response.data;
  },
};

// Genres API
// GET    /admin/genres                          -> { genres: [...], pagination }
// GET    /admin/genres/deleted                  -> { genres: [...], pagination, deleters: [...] }
// GET    /admin/genres/{id}                     -> { genre: {...} }
// GET    /admin/genres/{id}/neighbours?k        -> { genre, retrofit, embedding: [...], text_embedding: [...], identical, self_cosine, own_vector_moved, missing_vectors }
// GET    /admin/genres/compare?a&b              -> { a, b (each with missing_vectors), embedding_cosine, text_embedding_cosine, path, missing_entities }
// PUT    /admin/genres/{id}                     -> { success, message, updated_fields }
// DELETE /admin/genres/{id}?confirm&force       -> { success, message }
// GET    /admin/genres/wikidata                 -> { entries: [...], pagination }
// PUT    /admin/genres/{id}/wikidata-link       -> { success, message, genre_id, wikidata_entity_id, alias_added }
// POST   /admin/jobs/genre-delete              -> 202 { job_id, status, type }
// POST   /admin/jobs/genre-delete-bulk         -> 202 { job_id, status, type }
export const genresApi = {
  getAll: async (params = {}, config = {}) => {
    const response = await api.get('/admin/genres', { ...config, params });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/admin/genres/${id}`);
    return response.data;
  },

  update: async (id, data) => {
    const response = await api.put(`/admin/genres/${id}`, data);
    return response.data;
  },

  delete: async (id, force = false) => {
    const response = await api.delete(`/admin/genres/${id}`, {
      params: { confirm: true, ...(force && { force: true }) }
    });
    return response.data;
  },

  getWikidataList: async (params = {}) => {
    const response = await api.get('/admin/genres/wikidata', { params });
    return response.data;
  },

  getWikidataEntry: async (entityId) => {
    const response = await api.get(`/admin/genres/wikidata/${entityId}`);
    return response.data;
  },

  linkToWikidata: async (genreId, wikidataEntityId) => {
    const response = await api.put(`/admin/genres/${genreId}/wikidata-link`, {
      wikidata_entity_id: wikidataEntityId,
    });
    return response.data;
  },

  deleteWithReembed: async (genreId) => {
    const response = await api.post('/admin/jobs/genre-delete', { genre_id: genreId });
    return response.data;
  },

  bulkDeleteWithReembed: async (genreIds) => {
    const response = await api.post('/admin/jobs/genre-delete-bulk', { genre_ids: genreIds }, { params: { confirm: true } });
    return response.data;
  },

  getDeleted: async (params = {}) => {
    const response = await api.get('/admin/genres/deleted', { params });
    return response.data;
  },

  getArtists: async (genreId, params = {}) => {
    const response = await api.get(`/admin/genres/${genreId}/artists`, { params });
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/admin/genres/stats');
    return response.data;
  },

  getNeighbours: async (genreId, k = 10, config = {}) => {
    const response = await api.get(`/admin/genres/${genreId}/neighbours`, { ...config, params: { k } });
    return response.data;
  },

  compare: async (a, b, config = {}) => {
    const response = await api.get('/admin/genres/compare', { ...config, params: { a, b } });
    return response.data;
  },
};

// Translations API (#692, #839)
// GET /admin/translations?status=&locale=&kind=&search=&page=&page_size= -> { translations: [...], pagination }
//   status: needs_review (default) | failed | ok | same | all
// GET /admin/translations/summary                            -> { needs_review: {locale: n}, failed, needs_review_total, failed_total }
// PUT /admin/translations/{kind}/{key}/{locale} { text }     -> the row, now `ok` with `edited_by`
// POST .../retranslate {event_id?}                          -> a suggestion { text, status, review_reason, model, cost_usd }, never written
// POST .../approve · DELETE .../ (discard)                   -> { action, results, counts }
// POST /admin/translations/bulk {action, rows, estimate_only?, event_id?} -> the same shape, one result per row
const RETRANSLATE_TIMEOUT_MS = 180000;
const translationPath = ({ kind, key, locale }) =>
  `/admin/translations/${encodeURIComponent(kind)}/${encodeURIComponent(key)}/${encodeURIComponent(locale)}`;

export const translationsApi = {
  getAll: async (params = {}) => {
    const response = await api.get('/admin/translations', { params });
    return response.data;
  },

  getSummary: async () => {
    const response = await api.get('/admin/translations/summary');
    return response.data;
  },

  edit: async (row, text) => {
    const response = await api.put(translationPath(row), { text });
    return response.data;
  },

  // A suggestion `{text, status, review_reason}`, never written: save it with `edit` (#839). A 502
  // when the provider failed. Synchronous model calls, like classify: the API cancels what is still
  // running at 120s, inside this timeout, so a slow call is answered rather than abandoned while the
  // server goes on spending.
  retranslate: async (row, eventId) => {
    const response = await api.post(`${translationPath(row)}/retranslate`, eventId ? { event_id: eventId } : {}, {
      timeout: RETRANSLATE_TIMEOUT_MS,
    });
    return response.data;
  },

  approve: async (row) => {
    const response = await api.post(`${translationPath(row)}/approve`);
    return response.data;
  },

  discard: async (row) => {
    const response = await api.delete(translationPath(row));
    return response.data;
  },

  // One event's or venue's texts, each with its row per target locale (#840): `{type, id,
  // target_locales, texts: [{kind, key, source_text, uses: [{family, field}], translations: [{locale, row}]}]}`.
  getForDocument: async (type, id) => {
    const response = await api.get(`/admin/${type}s/${encodeURIComponent(id)}/translations`);
    return response.data;
  },

  // Translate now (#840): every text of the document with no row, or a failed one, straight away.
  // `{success, message, translations_requested, rows_written, by_status, cost_usd}`; a duplicate is a 409.
  translateDocument: async (type, id) => {
    const response = await api.post(`/admin/${type}s/${encodeURIComponent(id)}/translate`, null, {
      timeout: RETRANSLATE_TIMEOUT_MS,
    });
    return response.data;
  },

  bulk: async (action, rows, { estimateOnly = false } = {}) => {
    const response = await api.post(
      '/admin/translations/bulk',
      {
        action,
        rows: rows.map(({ kind, key, locale }) => ({ kind, key, locale })),
        ...(estimateOnly ? { estimate_only: true } : {}),
      },
      // The estimate reads the corpus when the example cache is cold; the retranslate also calls.
      { timeout: RETRANSLATE_TIMEOUT_MS }
    );
    return response.data;
  },
};

// Themes API
// GET /admin/themes/stats  -> { themes_total, distinct_used, top_n, top_n_share }
// GET /admin/themes        -> { themes: [...], pagination }
export const themesApi = {
  getStats: async () => {
    const response = await api.get('/admin/themes/stats');
    return response.data;
  },

  getAll: async (params = {}) => {
    const response = await api.get('/admin/themes', { params });
    return response.data;
  },
};

// Admin API
// GET  /health                                              -> { status, elasticsearch, postgresql }
// GET  /admin/stats                                        -> { timestamp, events: {...}, artists, venues, users }
// GET  /admin/pipeline-runs?start_date=&end_date=&limit=   -> { runs, total, limit, summary }
// GET  /admin/pipeline-runs/latest                         -> { run }
// POST /admin/jobs/pipeline                                -> { mode, message, ...execution or job data }
// POST /admin/jobs/fetch                                   -> 202 { job_id, status, type }
// GET  /admin/jobs/{job_id}                               -> { id, type, status, result_json, error, ... }
// GET  /admin/cleanup/occurrences/preview?cutoff_date=&stale_only= -> { events_affected, occurrences_to_remove, ... }
// POST /admin/cleanup/occurrences?cutoff_date=&stale_only= -> { success, events_cleaned, occurrences_removed, message }
export const adminApi = {
  getHealth: async () => {
    // Use validateStatus so 503 (degraded) resolves rather than throws
    const response = await api.get('/health', { validateStatus: () => true });
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/admin/stats');
    return response.data;
  },

  getPipelineRuns: async ({ limit = 25, startDate = null, endDate = null } = {}) => {
    const params = { limit };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await api.get('/admin/pipeline-runs', { params });
    return response.data;
  },

  runPipeline: async () => {
    const response = await api.post('/admin/jobs/pipeline');
    return response.data;
  },

  runQualityCheck: async () => {
    const response = await api.post('/admin/jobs/quality-check');
    return response.data;
  },

  getJobStatus: async (jobId) => {
    const response = await api.get(`/admin/jobs/${jobId}`);
    return response.data;
  },

  previewCleanup: async (cutoffDate, staleOnly = false) => {
    const response = await api.get('/admin/cleanup/occurrences/preview', {
      params: { cutoff_date: cutoffDate, stale_only: staleOnly },
    });
    return response.data;
  },

  executeCleanup: async (cutoffDate, staleOnly = false) => {
    const response = await api.post('/admin/cleanup/occurrences', null, {
      params: { cutoff_date: cutoffDate, stale_only: staleOnly },
      timeout: 120000,
    });
    return response.data;
  },
};

// Settings API
// GET /admin/login-methods            -> { email, spotify, google, updated_at, updated_by }
// PUT /admin/login-methods {email?,spotify?,google?} -> { email, spotify, google, updated_at, updated_by }
export const settingsApi = {
  getLoginMethods: async () => {
    const response = await api.get('/admin/login-methods');
    return response.data;
  },

  updateLoginMethods: async (data) => {
    const response = await api.put('/admin/login-methods', data);
    return response.data;
  },

  // GET /admin/promotion-settings            -> { promote_unclassified_events, updated_at, updated_by }
  // PUT /admin/promotion-settings {promote_unclassified_events?} -> same shape
  getPromotionSettings: async () => {
    const response = await api.get('/admin/promotion-settings');
    return response.data;
  },

  updatePromotionSettings: async (data) => {
    const response = await api.put('/admin/promotion-settings', data);
    return response.data;
  },

  // GET /admin/classification-settings -> { block_events_with_possible_duplicate_artists, updated_at, updated_by }
  // PUT /admin/classification-settings {block_events_with_possible_duplicate_artists?} -> same shape
  getClassificationSettings: async () => {
    const response = await api.get('/admin/classification-settings');
    return response.data;
  },

  updateClassificationSettings: async (data) => {
    const response = await api.put('/admin/classification-settings', data);
    return response.data;
  },

  // GET /admin/occurrence-dedup-settings -> { enabled, dedup_providers, updated_at, updated_by }
  // PUT /admin/occurrence-dedup-settings {enabled?, dedup_providers?} -> same shape
  getOccurrenceDedupSettings: async () => {
    const response = await api.get('/admin/occurrence-dedup-settings');
    return response.data;
  },

  updateOccurrenceDedupSettings: async (data) => {
    const response = await api.put('/admin/occurrence-dedup-settings', data);
    return response.data;
  },

  // GET /admin/localized-content-settings -> { enabled_locales, available_locales, updated_at, updated_by }
  // PUT /admin/localized-content-settings {enabled_locales} (the whole list) -> same shape
  getLocalizedContentSettings: async () => {
    const response = await api.get('/admin/localized-content-settings');
    return response.data;
  },

  updateLocalizedContentSettings: async (data) => {
    const response = await api.put('/admin/localized-content-settings', data);
    return response.data;
  },

  // GET /admin/curator-settings -> { mode, audience, daily_budget_usd, shortlist_size,
  //   pick_size, cache_ttl_hours, timeout_seconds, updated_at, updated_by }
  // PUT /admin/curator-settings {any subset} -> same shape
  getCuratorSettings: async () => {
    const response = await api.get('/admin/curator-settings');
    return response.data;
  },

  updateCuratorSettings: async (data) => {
    const response = await api.put('/admin/curator-settings', data);
    return response.data;
  },

  // GET /admin/curator-users?limit=&offset= -> { users: [...], total }
  getCuratorUsers: async ({ limit = 25, offset = 0, search = '' } = {}) => {
    const params = { limit, offset };
    if (search) params.search = search;
    const response = await api.get('/admin/curator-users', { params });
    return response.data;
  },

  // PUT /admin/curator-users/{id} {enabled?, daily_budget_usd?, clear_budget?} -> the row
  setCuratorUser: async (userId, data) => {
    const response = await api.put(`/admin/curator-users/${userId}`, data);
    return response.data;
  },

  // GET /admin/curator-usage?start_date=&end_date= -> { summary, by_day, by_user, fallbacks,
  //   mode, daily_budget_usd, spend_today }
  getCuratorUsage: async ({ startDate, endDate } = {}) => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    const response = await api.get('/admin/curator-usage', { params });
    return response.data;
  },

  // GET /admin/opendata-madrid-settings -> { include_types, include_untyped, updated_at, updated_by }
  // PUT /admin/opendata-madrid-settings {include_types?, include_untyped?} -> same shape
  getOpenDataMadridSettings: async () => {
    const response = await api.get('/admin/opendata-madrid-settings');
    return response.data;
  },

  updateOpenDataMadridSettings: async (data) => {
    const response = await api.put('/admin/opendata-madrid-settings', data);
    return response.data;
  },

  // Onboarding suggestions (curated, stored in app_settings).
  // GET  /admin/suggested-artists -> { base, by_city, updated_at, updated_by }
  // PUT  /admin/suggested-artists { base, by_city } -> same shape
  getSuggestedArtists: async () => {
    const response = await api.get('/admin/suggested-artists');
    return response.data;
  },

  updateSuggestedArtists: async (data) => {
    const response = await api.put('/admin/suggested-artists', data);
    return response.data;
  },

  // GET  /admin/suggested-themes -> { themes, updated_at, updated_by }
  // PUT  /admin/suggested-themes { themes } -> same shape
  getSuggestedThemes: async () => {
    const response = await api.get('/admin/suggested-themes');
    return response.data;
  },

  // Search canonical onboarding themes (same index the consumer onboarding search uses).
  // GET /onboarding/themes/search?query=&limit= -> { themes: [{id, name, cluster_id}] }
  searchThemes: async (query, limit = 8) => {
    const cleaned = String(query || '').trim();
    if (cleaned.length < 2) return [];
    const response = await api.get('/onboarding/themes/search', { params: { query: cleaned, limit } });
    return response.data.themes || [];
  },

  updateSuggestedThemes: async (data) => {
    const response = await api.put('/admin/suggested-themes', data);
    return response.data;
  },
};

// Cities API
// GET   /admin/cities/sources -> { cities: [{ city_name, active, enabled_sources, timezone, default_language }], supported_locales }
// PATCH /admin/cities/{city}/sources { jambase: false } -> updated city
// PATCH /admin/cities/{city}/active  { active: false }  -> updated city
// PATCH /admin/cities/{city}/timezone { timezone: 'Europe/Madrid' } -> updated city
// PATCH /admin/cities/{city}/default-language { default_language: 'es' } -> updated city
export const citiesApi = {
  // `supportedLocales` is the API's SUPPORTED_LOCALES — what a city's default language may be.
  getSources: async () => {
    const response = await api.get('/admin/cities/sources');
    return { cities: response.data.cities || [], supportedLocales: response.data.supported_locales || [] };
  },

  updateSources: async (cityName, patch) => {
    const response = await api.patch(`/admin/cities/${encodeURIComponent(cityName)}/sources`, patch);
    return response.data;
  },

  updateCity: async (cityName, patch) => {
    const response = await api.patch(`/admin/cities/${encodeURIComponent(cityName)}/active`, patch);
    return response.data;
  },

  // The zone the city's venues keep — what the API calls "today" when deciding which
  // occurrences are past. An empty string clears it back to the UTC-day fallback.
  updateTimezone: async (cityName, timezone) => {
    const response = await api.patch(`/admin/cities/${encodeURIComponent(cityName)}/timezone`, { timezone });
    return response.data;
  },

  // The language the city's content is natively written in. An empty string clears it.
  updateDefaultLanguage: async (cityName, language) => {
    const response = await api.patch(`/admin/cities/${encodeURIComponent(cityName)}/default-language`, {
      default_language: language,
    });
    return response.data;
  },
};

// GET   /admin/ticket-providers -> { providers: [{ provider, blocked, cities, event_count, ... }] }
// PATCH /admin/ticket-providers/{provider} { blocked: true } -> updated provider
export const ticketProvidersApi = {
  getAll: async () => {
    const response = await api.get('/admin/ticket-providers');
    return Array.isArray(response.data) ? response.data : response.data.providers || [];
  },

  setBlocked: async (provider, blocked) => {
    const response = await api.patch(`/admin/ticket-providers/${encodeURIComponent(provider)}`, { blocked });
    return response.data;
  },

  // Human judgement only — the registry refresh never writes this column.
  setCategory: async (provider, category) => {
    const response = await api.patch(
      `/admin/ticket-providers/${encodeURIComponent(provider)}/category`,
      { category }
    );
    return response.data;
  },

  // Only for a provider whose row carries `image_rule` (it has a provider image rule): whether
  // that rule applies its image without an admin's click. -> { provider, image_rule: { auto_apply } }
  setImageRule: async (provider, autoApply) => {
    const response = await api.patch(
      `/admin/ticket-providers/${encodeURIComponent(provider)}/image-rule`,
      { auto_apply: autoApply }
    );
    return response.data;
  },

  // Rebuild identity/reach from the event index. Normally a pipeline step; this is the manual run.
  refresh: async () => {
    const response = await api.post('/admin/ticket-providers/refresh');
    return response.data;
  },
};

export default api;
